import { IonCard, IonCardContent, IonContent, IonIcon, IonInput, IonPopover, IonSelect, IonSelectOption } from '@ionic/react'
import { SearchButton } from 'components/search-button'
import { SearchToolbarFormItem } from 'components/search-toolbar-form-item'
import './search-form.scss'
import { authContext } from 'contexts'
import { useCities, useProfileTypes } from 'hooks'
import { icon_calendar, icon_info } from 'icons'
import { ISearchParams } from 'interfaces'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { dateService } from 'services'
import { v4 as uuidv4 } from 'uuid'
import { DatesPickerModal } from 'components/dates-picker-modal'
import { useLocation } from 'react-router'
import qs from 'qs'
import { ISearchToolbarProps } from './search-toolbar-props.interface'
const MAX_NUMBER_GUESTS = process.env.REACT_APP_SEARCH_MAX_NUMBER_GUESTS

const SearchForm: React.FC<ISearchToolbarProps> = ({ defaultParams, onSubmitFunction }) => {
  const auth = useContext(authContext)
  const isAuthenticated = !!auth?.user
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useState<ISearchParams | undefined>(defaultParams)
  const [datesPickerModalOpen, setDatesPickerModalOpen] = useState(false)
  const maxGuests = parseInt(MAX_NUMBER_GUESTS ?? '0')
  const [datesPlaceholder, setDatesPlaceholder] = useState<string>('')
  const popover = useRef<HTMLIonPopoverElement>(null)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const openPopover = (e: any) => {
    popover.current!.event = e
    setPopoverOpen(true)
  }

  const { search } = useLocation()

  const guestsOptions = useMemo(() => {
    const result = []
    for (let i = 1; i <= maxGuests; i++) {
      result.push({ id: icon_calendar, value: i, key: uuidv4() })
    }
    return result
  }, [])
  const profileTypes = useProfileTypes()
  const cities = useCities()
  useEffect(() => {
    const search_params: ISearchParams = {}

    if (search) {
      const query_string = qs.parse(search.substring(1))

      if (defaultParams?.profileTypeId) search_params.profileTypeId = defaultParams?.profileTypeId

      if (!!!defaultParams?.profileTypeId && query_string.p) search_params.profileTypeId = query_string.p as string
      if (query_string.c) search_params.cityId = query_string.c as string
      if (query_string.df) search_params.dateFrom = dateService.baseParseDateString(query_string.df as string)
      if (query_string.dt) search_params.dateTo = dateService.baseParseDateString(query_string.dt as string)
      if (query_string.ce) search_params.eventCode = query_string.ce as string
      if (query_string.r && (query_string.r as string[]).length > 0)
        search_params.guestsPerRoom = (query_string.r as string[]).map((gpr) => parseInt(gpr))

      setSearchParams(search_params)
    } else {
      if (isAuthenticated) {
        defaultParams!.profileTypeId = auth?.getUserData()!.tipoProfilo.id
      }
      setSearchParams({ ...defaultParams })
    }
  }, [defaultParams, profileTypes, cities, search, isAuthenticated, auth])

  useEffect(() => {
    const dates_placeholder =
      searchParams?.dateFrom || searchParams?.dateTo
        ? dateService.formatDatePickerPlaceholder(searchParams?.dateFrom) +
          ' - ' +
          dateService.formatDatePickerPlaceholder(searchParams?.dateTo)
        : ''
    setDatesPlaceholder(dates_placeholder)
  }, [searchParams])

  const submitSearch = () => {
    onSubmitFunction(searchParams!)
  }

  const onChangeMobileDates = (dateFrom?: Date | undefined, dateTo?: Date | undefined) => {
    const new_search_params = { ...searchParams }
    if (dateFrom) new_search_params.dateFrom = dateFrom
    if (dateTo) new_search_params.dateTo = dateTo
    setSearchParams(new_search_params)
  }

  return (
    <IonCard className="ionCard">
      <IonCardContent className="content">
        {searchParams?.dateFrom && searchParams.dateTo && (
          <DatesPickerModal
            dateFrom={searchParams.dateFrom}
            dateTo={searchParams.dateTo}
            onConfirm={onChangeMobileDates}
            isOpen={datesPickerModalOpen}
            setIsOpen={setDatesPickerModalOpen}
          />
        )}
        <SearchToolbarFormItem label={t('profile')}>
          <IonSelect
            disabled={isAuthenticated}
            className="search-dropdown"
            selectedText={
              searchParams?.profileTypeId ? profileTypes.find((pt) => pt.id === searchParams.profileTypeId)?.descrizione : t('teachers')
            }
            interface="popover"
            value={searchParams?.profileTypeId ? searchParams?.profileTypeId : 'C'}
            onIonChange={(ev) => {
              const selectedProfileTypeId = ev.detail.value
              setSearchParams({ ...searchParams, profileTypeId: selectedProfileTypeId })
            }}
          >
            {profileTypes.map((pt) => (
              <IonSelectOption value={pt.id} key={pt.id}>
                {pt.descrizione}
              </IonSelectOption>
            ))}
          </IonSelect>
        </SearchToolbarFormItem>

        <SearchToolbarFormItem label={t('city')}>
          <IonSelect
            className=" search-dropdown"
            selectedText={searchParams?.cityId ? cities.find((c) => c.id === searchParams.cityId)?.nome : t('select_city')}
            interface="popover"
            onIonChange={(ev) => {
              setSearchParams({ ...searchParams, cityId: cities.find((c) => c.id === ev.detail.value)?.id })
            }}
          >
            {cities.map((c) => (
              <IonSelectOption value={c.id} key={c.id}>
                {c.nome}
              </IonSelectOption>
            ))}
          </IonSelect>
        </SearchToolbarFormItem>

        <SearchToolbarFormItem label={t('check_in') + ' / ' + t('check_out')} icon={{ icon: icon_calendar, size: 'large' }}>
          <IonInput
            placeholder={t('select_dates')}
            value={datesPlaceholder}
            onClick={() => {
              if (datesPickerModalOpen) {
                setDatesPickerModalOpen(false)
              } else {
                setDatesPickerModalOpen(true)
              }
            }}
          />
        </SearchToolbarFormItem>

        <SearchToolbarFormItem label={t('people')}>
          <IonSelect
            className=" search-dropdown"
            value={searchParams?.guestsPerRoom ? searchParams.guestsPerRoom[0] : 1}
            interface="popover"
            onIonChange={(ev) => {
              setSearchParams({ ...searchParams, guestsPerRoom: [parseInt(ev.target.value)] })
            }}
          >
            {guestsOptions.map((c: any) => (
              <IonSelectOption value={c.value} key={c.key}>
                {c.value}
              </IonSelectOption>
            ))}
          </IonSelect>
        </SearchToolbarFormItem>

        <SearchToolbarFormItem label={t('event_code')}>
          <IonInput
            onIonInput={(e) => setSearchParams({ ...searchParams, eventCode: e.detail.value?.toUpperCase() ?? undefined })}
            value={searchParams?.eventCode}
          />
          <IonIcon onClick={openPopover} icon={icon_info} size="large"></IonIcon>
          <IonPopover
            className="popover"
            ref={popover}
            isOpen={popoverOpen}
            onDidDismiss={() => setPopoverOpen(false)}
            side="bottom"
            alignment="center"
          >
            <IonContent className="popover_content">{t('event_code_condition')}</IonContent>
          </IonPopover>
        </SearchToolbarFormItem>
        <div className="searchButton">
          <SearchButton onClickFunction={() => submitSearch()}></SearchButton>
        </div>
      </IonCardContent>
    </IonCard>
  )
}
export default SearchForm
